import type { ComponentType, CSSProperties } from 'react'
import clsx from 'clsx'
import { NavLink } from 'react-router-dom'

import { Bookmark, Bulb, Link, Monitor, Shield, User, type GlyphProps } from './icons'
import type { Tone } from './model'

// ============================================================================
// THE SETTINGS COLUMN — 236px, white, 1px right border (README §Shared shell:
// "262px on Chat, 236px on Connections").
//
// ALL SIX ROWS NOW LEAD SOMEWHERE (2026-08-28). Until this commit four of them
// were inert, marked unavailable rather than deleted. What changed is not that
// four backends appeared — three of the four screens are mostly a statement of
// what does NOT exist — but that "we know, and not yet" is worth a screen and is
// not worth a dead nav row. Each screen states its own absences in words; none
// of them ships a control that looks pressable and is not.
// ============================================================================

/** Which of the settings screens is on screen. */
export type SettingsPage =
  | 'account'
  | 'connections'
  | 'privacy'
  | 'journal'
  | 'notifications'
  | 'appearance'

interface NavRow {
  label: string
  icon: ComponentType<GlyphProps>
  page: SettingsPage
  to: string
}

const ROWS: NavRow[] = [
  { label: 'Account', icon: User, page: 'account', to: '/settings/account' },
  { label: 'Connections', icon: Link, page: 'connections', to: '/settings/connections' },
  { label: 'Privacy & data', icon: Shield, page: 'privacy', to: '/settings/privacy' },
  { label: 'Journal defaults', icon: Bookmark, page: 'journal', to: '/settings/journal' },
  { label: 'Notifications', icon: Bulb, page: 'notifications', to: '/settings/notifications' },
  { label: 'Appearance', icon: Monitor, page: 'appearance', to: '/settings/appearance' },
]

const DOT: Record<Tone, string> = {
  danger: 'bg-danger-dot',
  warning: 'bg-warning-dot',
  neutral: 'bg-muted',
}

export function SettingsNav({
  /** Which built settings screen the nav is standing in. */
  active = 'connections',
  /** The worst current state, or null when there is genuinely nothing to flag. */
  tone,
  /** What the dot means, for anyone who cannot see a 6px circle. */
  toneReason,
  demoMode,
}: {
  active?: SettingsPage
  tone: Tone | null
  toneReason: string | null
  demoMode: boolean
}) {
  return (
    <nav
      aria-label="Settings"
      className="flex w-[236px] flex-none flex-col border-r border-line bg-surface px-3 py-[18px]"
    >
      <h1 className="px-[6px] pb-[14px] text-[14px] font-semibold leading-none tracking-[-0.01em]">
        Settings
      </h1>

      <ul className="flex flex-col gap-[2px]">
        {ROWS.map((row) => {
          const { label, icon: Icon, page, to } = row
          const here = page === active
          const rowClass = clsx(
            'mo-rule flex items-center gap-[10px] rounded-tile px-[11px] py-[9px]',
            here ? 'bg-fill' : 'text-ink-2',
          )
          const body = (
            <>
              <Icon size={15} strokeWidth={1.8} className={here ? 'text-ink' : 'text-muted'} />
              <span
                className={clsx(
                  'text-[12.5px] leading-none',
                  here ? 'font-semibold text-ink' : 'font-medium text-ink-2',
                )}
              >
                {label}
              </span>
              {here && tone && (
                <>
                  <span className={clsx('ml-auto size-[6px] rounded-full', DOT[tone])} aria-hidden />
                  <span className="sr-only">{toneReason}</span>
                </>
              )}
            </>
          )
          return (
            <li key={label}>
              <NavLink
                to={to}
                aria-current={here ? 'page' : undefined}
                style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
                className={rowClass}
              >
                {body}
              </NavLink>
            </li>
          )
        })}
      </ul>

      <Footnote demoMode={demoMode} />
    </nav>
  )
}

/**
 * THE FOOTNOTE IS A PROMISE, or a warning. In the ordinary case it states the
 * rule the whole screen exists to keep — nothing syncs silently. When demo mode
 * is on it says so HERE as well as in the page banner, because the one place the
 * owner must never be able to miss it is the nav he is standing in (README §12:
 * "Demo mode announces itself in a page banner and in the settings nav, never
 * only in a toggle").
 */
function Footnote({ demoMode }: { demoMode: boolean }) {
  return (
    <div
      className={clsx(
        'mt-auto flex flex-col gap-[5px] rounded-row border px-3 py-[11px]',
        demoMode ? 'border-warning-line bg-warning-fill' : 'border-fill bg-surface-2',
      )}
    >
      <div
        className={clsx(
          'text-[10.5px] font-semibold leading-none',
          demoMode ? 'text-warning' : 'text-ink-2',
        )}
      >
        {demoMode ? 'Demo mode is on' : 'Nothing syncs silently'}
      </div>
      <div
        className={clsx(
          'text-[10.5px] font-normal leading-[1.45]',
          demoMode ? 'text-warning-2' : 'text-microcopy',
        )}
      >
        {demoMode
          ? 'Every screen is showing the sample dataset, not your life.'
          : 'Every pull is logged with a timestamp you can see here.'}
      </div>
    </div>
  )
}
